import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { formatImageUrl } from '../utils/formatImageUrl';

const GlobalContext = createContext();

export const GlobalProvider = ({ children }) => {
    const [cartItems, setCartItems] = useState([]);
    const [wishlistItems, setWishlistItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [globalSearchQuery, setGlobalSearchQuery] = useState("");

    const fetchCart = useCallback(async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            // Guest mode: Read from localStorage
            try {
                const guestCart = localStorage.getItem("guest_cart");
                if (guestCart) {
                    const parsed = JSON.parse(guestCart);
                    setCartItems(Array.isArray(parsed) ? parsed : []);
                } else {
                    setCartItems([]);
                }
            } catch (e) {
                console.error("Failed to parse guest cart", e);
                setCartItems([]);
            }
            return;
        }

        try {
            const res = await api.get('/api/cart');
            if (Array.isArray(res.data)) {
                setCartItems(res.data.map(item => {
                    const liveVariant = item.selectedVariantId && item.product?.Variants
                        ? item.product.Variants.find(v => String(v.id) === String(item.selectedVariantId))
                        : null;
                    const liveVariantImg = liveVariant ? (liveVariant.image || (liveVariant.images && liveVariant.images[0])) : null;
                    const rawImg = liveVariantImg || item.productSnapshot?.image || item.product?.image;
                    const maxStock = liveVariant 
                        ? (liveVariant.stock !== undefined && liveVariant.stock !== null ? Number(liveVariant.stock) : 999)
                        : (item.product?.stock !== undefined && item.product?.stock !== null ? Number(item.product.stock) : 999);

                    const shippingWeight = (liveVariant?.shippingWeight !== null && liveVariant?.shippingWeight !== undefined && liveVariant?.shippingWeight !== '')
                        ? liveVariant.shippingWeight
                        : (item.product?.shippingWeight !== null && item.product?.shippingWeight !== undefined ? item.product.shippingWeight : null);

                    const shippingDimensions = liveVariant?.shippingDimensions || item.product?.shippingDimensions || null;

                    return {
                        cartItemId: item.id,
                        id: item.productId,
                        productId: item.productId,
                        name: item.productSnapshot?.name || item.product?.name,
                        price: liveVariant ? (liveVariant.salesPrice || liveVariant.mrp) : (item.productSnapshot?.price || item.product?.price),
                        image: formatImageUrl(rawImg, '/assets/images/thumbs/product-img1.png'),
                        quantity: item.quantity,
                        stock: maxStock,
                        selectedVariantId: item.selectedVariantId,
                        selectedProductColor: item.selectedProductColor,
                        selectedProductSize: item.selectedProductSize,
                        variantName: item.productSnapshot?.selectedVariantName || (liveVariant ? liveVariant.variantName : null),
                        product: item.product,
                        variant: liveVariant,
                        shippingWeight,
                        shippingDimensions,
                    };
                }));
            }
        } catch (err) {
            if (err.response && err.response.status === 401) {
                // Ignore 401 errors for unauthenticated users
                return;
            }
            console.error("Failed to fetch cart", err);
        }
    }, []);

    const fetchWishlist = useCallback(async () => {
        try {
            const res = await api.get('/api/wishlist');
            if (Array.isArray(res.data)) {
                setWishlistItems(res.data.map(item => {
                    const prod = item.product || {};
                    const v = item.variant || null;

                    let rawImg = v?.image || (Array.isArray(v?.images) && v.images[0]) || (Array.isArray(prod?.image) ? prod.image[0] : prod?.image) || '';
                    let img = formatImageUrl(rawImg, '/assets/images/thumbs/product-img1.png');

                    let unit = "";
                    if (v) {
                        unit = v.variantName || v.unit || "";
                        unit = unit.replace(/(?:Custom Note|Volume|Weight|Size|Colour|Color|Variant)\s*:\s*/gi, '').trim();
                        if (unit.toLowerCase() === 'default') unit = "";
                    }

                    const salesPrice = v?.salesPrice || prod.price || 0;
                    const mrp = v?.mrp || prod.price || salesPrice;

                    return {
                        wishlistItemId: item.id,
                        id: item.productId,
                        productId: item.productId,
                        variantId: item.variantId || (v ? v.id : null),
                        selectedVariant: v,
                        variantName: v?.variantName || null,
                        name: prod.name || item.productSnapshot?.name || 'Product',
                        productName: prod.name || 'Product',
                        price: salesPrice,
                        currentPrice: `₹${Math.round(salesPrice)}`,
                        originalPrice: mrp > salesPrice ? `₹${Math.round(mrp)}` : '',
                        quantityText: unit ? `/ ${unit.toUpperCase()}` : '',
                        image: img || 'assets/images/thumbs/product-img1.png',
                        product: prod,
                        rawVariants: prod.Variants || []
                    };
                }));
            }
        } catch (err) {
            if (err.response && err.response.status === 401) {
                // Ignore 401 errors for unauthenticated users
                return;
            }
            console.error("Failed to fetch wishlist", err);
        }
    }, []);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await api.get('/api/categories/categories');
                if (response.data && response.data.success) {
                    setCategories(response.data.data.filter(c => c.isActive).sort((a, b) => a.sortOrder - b.sortOrder));
                }
            } catch (err) {
                console.error("Failed to fetch global categories", err);
            }
        };

        fetchCategories();
        fetchCart();
        fetchWishlist();
    }, [fetchCart, fetchWishlist]);

    const addToCart = async (product, quantity = 1, selectedVariant = null) => {
        const variantToUse = selectedVariant 
            || product.defaultVariant 
            || (product.rawVariants && product.rawVariants.length > 0 ? (product.rawVariants.find(v => v.isDefault === true || v.is_default === true || String(v.isDefault) === 'true' || String(v.is_default) === 'true') || product.rawVariants[0]) : null)
            || (product.Variants && product.Variants.length > 0 ? (product.Variants.find(v => v.isDefault === true || v.is_default === true || String(v.isDefault) === 'true' || String(v.is_default) === 'true') || product.Variants[0]) : null);

        const token = localStorage.getItem("token");

        if (!token) {
            // Guest User: Store in localStorage
            try {
                const rawStored = localStorage.getItem("guest_cart");
                let localCart = rawStored ? JSON.parse(rawStored) : [];
                if (!Array.isArray(localCart)) localCart = [];

                const vId = variantToUse ? variantToUse.id : null;
                const existingIndex = localCart.findIndex(i => 
                    String(i.id || i.productId) === String(product.id) &&
                    (vId ? String(i.selectedVariantId) === String(vId) : !i.selectedVariantId)
                );

                const maxStock = variantToUse 
                    ? (variantToUse.stock !== undefined && variantToUse.stock !== null ? Number(variantToUse.stock) : 999)
                    : (product.stock !== undefined && product.stock !== null ? Number(product.stock) : 999);

                if (existingIndex > -1) {
                    const newQty = localCart[existingIndex].quantity + quantity;
                    if (newQty > maxStock) {
                        toast.error(`Maximum available stock (${maxStock}) reached`);
                        return;
                    }
                    localCart[existingIndex].quantity = newQty;
                } else {
                    if (quantity > maxStock) {
                        toast.error(`Only ${maxStock} items available in stock`);
                        return;
                    }

                    const rawImg = (variantToUse && variantToUse.image)
                        ? variantToUse.image
                        : (variantToUse && variantToUse.images && variantToUse.images.length > 0 ? variantToUse.images[0] : (product.image || ''));

                    const unitPrice = variantToUse 
                        ? (variantToUse.salesPrice || variantToUse.mrp || 0)
                        : (product.price || product.currentPrice || 0);

                    const cleanPrice = typeof unitPrice === 'string' ? parseFloat(unitPrice.replace(/[^0-9.]/g, '')) : Number(unitPrice);

                    const newItem = {
                        cartItemId: 'guest_' + Date.now() + '_' + Math.random().toString(36).substring(2, 7),
                        id: product.id,
                        productId: product.id,
                        name: product.name || product.title || 'Product',
                        price: isNaN(cleanPrice) ? 0 : cleanPrice,
                        image: formatImageUrl(rawImg, '/assets/images/thumbs/product-img1.png'),
                        quantity: quantity,
                        stock: maxStock,
                        selectedVariantId: vId,
                        selectedProductColor: product.selectedProductColor || null,
                        selectedProductSize: product.selectedProductSize || null,
                        variantName: variantToUse ? (variantToUse.variantName || variantToUse.unit || null) : null,
                        product: product,
                        variant: variantToUse,
                        shippingWeight: (variantToUse?.shippingWeight !== null && variantToUse?.shippingWeight !== undefined && variantToUse?.shippingWeight !== '')
                            ? variantToUse.shippingWeight
                            : (product.shippingWeight !== null && product.shippingWeight !== undefined ? product.shippingWeight : null),
                        shippingDimensions: variantToUse?.shippingDimensions || product.shippingDimensions || null,
                    };
                    localCart.push(newItem);
                }

                localStorage.setItem("guest_cart", JSON.stringify(localCart));
                setCartItems(localCart);
                toast.success(`${product.name || product.title || 'Product'} added to Cart!`);
            } catch (e) {
                console.error("Failed to add to guest cart", e);
                toast.error("Failed to add to cart");
            }
            return;
        }

        // Authenticated User: Add to backend cart
        try {
            const payload = {
                productId: product.id,
                quantity: quantity
            };
            if (variantToUse) {
                payload.selectedVariantId = variantToUse.id;
                payload.selectedVariantName = variantToUse.variantName;
            }
            const res = await api.post('/api/cart/add', payload);
            if (res.status === 201 || res.status === 200) {
                toast.success(`${product.name || product.title || 'Product'} added to Cart!`);
                fetchCart(); // Refresh cart
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to add to cart');
        }
    };

    const removeFromCart = async (cartItemId) => {
        const token = localStorage.getItem("token");
        if (!token) {
            // Guest User: Remove from localStorage
            try {
                const rawStored = localStorage.getItem("guest_cart");
                let localCart = rawStored ? JSON.parse(rawStored) : [];
                if (Array.isArray(localCart)) {
                    localCart = localCart.filter(i => i.cartItemId !== cartItemId);
                    localStorage.setItem("guest_cart", JSON.stringify(localCart));
                    setCartItems(localCart);
                    toast.success('Item removed from cart');
                }
            } catch (e) {
                console.error("Failed to remove guest cart item", e);
            }
            return;
        }

        const itemToRemove = cartItems.find(i => i.cartItemId === cartItemId);
        if (!itemToRemove) return;

        try {
            // optimistically update UI
            setCartItems(prev => prev.filter(i => i.cartItemId !== cartItemId));
            await api.delete(`/api/cart/remove/${cartItemId}`);
            toast.success('Item removed from cart');
        } catch (err) {
            toast.error('Failed to remove item');
            fetchCart();
        }
    };

    const updateCartQuantity = async (cartItemId, quantity) => {
        if (quantity < 1) quantity = 1;
        const item = cartItems.find(i => i.cartItemId === cartItemId);
        if (!item) return;

        const currentQty = item.quantity;
        const maxStock = item.stock !== undefined && item.stock !== null ? Number(item.stock) : 999;

        // Prevent increasing if greater than available stock
        if (quantity > currentQty && quantity > maxStock) {
            toast.error(`Maximum available stock (${maxStock}) reached`);
            return;
        }

        const token = localStorage.getItem("token");
        if (!token) {
            // Guest User: Update in localStorage
            try {
                const rawStored = localStorage.getItem("guest_cart");
                let localCart = rawStored ? JSON.parse(rawStored) : [];
                if (Array.isArray(localCart)) {
                    localCart = localCart.map(i => i.cartItemId === cartItemId ? { ...i, quantity } : i);
                    localStorage.setItem("guest_cart", JSON.stringify(localCart));
                    setCartItems(localCart);
                }
            } catch (e) {
                console.error("Failed to update guest cart qty", e);
            }
            return;
        }

        // Optimistic update for logged in user
        setCartItems(prev => prev.map(i => i.cartItemId === cartItemId ? { ...i, quantity } : i));

        try {
            if (quantity > currentQty) {
                await api.patch(`/api/cart/increase/${item.cartItemId}`);
            } else if (quantity < currentQty) {
                await api.patch(`/api/cart/decrease/${item.cartItemId}`);
            }
            fetchCart();
        } catch (err) {
            fetchCart();
            // Suppress toast for stock limit error since the button should be disabled
            const errMsg = err.response?.data?.message || '';
            if (!errMsg.toLowerCase().includes("stock")) {
                toast.error(errMsg || 'Failed to update quantity');
            }
        }
    };

    // Synchronize guest cart items into user's account upon login/register
    const syncGuestCart = async () => {
        try {
            const guestCart = localStorage.getItem("guest_cart");
            if (!guestCart) return;
            const items = JSON.parse(guestCart);
            if (Array.isArray(items) && items.length > 0) {
                for (const item of items) {
                    try {
                        await api.post('/api/cart/add', {
                            productId: item.productId || item.id,
                            quantity: item.quantity || 1,
                            selectedVariantId: item.selectedVariantId || null,
                            selectedVariantName: item.variantName || null,
                            selectedProductColor: item.selectedProductColor || null,
                            selectedProductSize: item.selectedProductSize || null,
                        });
                    } catch (addErr) {
                        console.error("Error syncing item to cart", item, addErr);
                    }
                }
                localStorage.removeItem("guest_cart");
            }
        } catch (e) {
            console.error("Failed to sync guest cart", e);
        }
    };

    const addToWishlist = async (product, selectedVariant = null) => {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Please login to add items to your wishlist.");
            return;
        }

        const variantToUse = selectedVariant 
            || product.selectedVariant 
            || product.defaultVariant 
            || (product.rawVariants && product.rawVariants.length > 0 ? (product.rawVariants.find(v => v.isDefault || v.is_default) || product.rawVariants[0]) : null);

        const vId = variantToUse ? variantToUse.id : null;

        const alreadyIn = wishlistItems.some(item => 
            String(item.id) === String(product.id) && 
            (vId ? String(item.variantId) === String(vId) : !item.variantId)
        );

        if (alreadyIn) {
            toast.success(`${product.name || 'Item'} is already in Wishlist!`, { icon: 'ℹ️' });
            return;
        }

        try {
            const res = await api.post('/api/wishlist/add', {
                productId: product.id,
                variantId: vId
            });

            if (res.status === 201 || res.status === 200) {
                const label = variantToUse?.variantName 
                    ? `${product.name || 'Product'} (${variantToUse.variantName})`
                    : (product.name || 'Product');
                toast.success(`${label} added to Wishlist!`, { icon: '❤️' });
                fetchWishlist();
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to add to wishlist');
        }
    };

    const removeFromWishlist = async (productId, variantId = null) => {
        let item = null;
        if (variantId) {
            item = wishlistItems.find(i => String(i.id) === String(productId) && String(i.variantId) === String(variantId));
        }
        if (!item) {
            item = wishlistItems.find(i => String(i.id) === String(productId));
        }

        if (!item?.wishlistItemId) return;

        try {
            setWishlistItems(prev => prev.filter(i => i.wishlistItemId !== item.wishlistItemId));
            await api.delete(`/api/wishlist/remove/${item.wishlistItemId}`);
            toast.success('Removed from wishlist');
        } catch (err) {
            toast.error('Failed to remove from wishlist');
            fetchWishlist();
        }
    };

    return (
        <GlobalContext.Provider value={{
            cartItems, addToCart, removeFromCart, updateCartQuantity,
            wishlistItems, addToWishlist, removeFromWishlist,
            categories,
            globalSearchQuery, setGlobalSearchQuery,
            fetchCart, fetchWishlist, syncGuestCart
        }}>
            {children}
        </GlobalContext.Provider>
    );
};

export const useGlobal = () => useContext(GlobalContext);
